"use client";

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";

import Link from "next/link";
import { useMemo } from "react";
import { Heading, HeadingProps } from "@repo/ui/heading";
import { Icon } from "@repo/ui/icon";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { BaseImage, absoluteCmsUrl } from "../base-image/BaseImage";

export type StrapiImageFormat = {
  ext: string;
  url: string;
  width: number;
  height: number;
  mime: string;
  name: string;
  size: number;
};

export type StrapiMedia = {
  id: number;
  url: string;
  width: number;
  height: number;
  name: string;
  alternativeText?: string | null;
  caption?: string | null;
  formats?: {
    large?: StrapiImageFormat;
    medium?: StrapiImageFormat;
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
};

export type SliderHeading = {
  id: number;
  description?: any;
  align?: "left" | "center" | "right" | null;
  heading: HeadingProps;
};

export type SliderCard = {
  id: number;
  title: string;
  subtitle?: string | null;
  description?: any;
  url?: string | null;
  image?: StrapiMedia | null;
};

export type SliderCta = {
  id: number;
  title: string;
  url: string;
  target?: string | null;
};

export type SliderItemsProps = {
  headline?: SliderHeading | null;
  items: SliderCard[];
  link?: SliderCta | null;
};

export function slugifyTitle(title?: string | null): string | undefined {
  if (!title) return undefined;

  return title
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
}

const FadePlugin = (slider: any) => {
  const update = () => {
    const d = slider.track?.details;
    if (!d) return;
    slider.slides.forEach((el: HTMLElement, i: number) => {
      const portion = d.slides?.[i]?.portion ?? 0;
      const opacity = 0.4 + 0.6 * portion;
      el.style.opacity = String(opacity);
      el.style.transition = "opacity 200ms";
    });
  };
  slider.on("created", update);
  slider.on("detailsChanged", update);
};

export default function SliderItems({
  headline,
  items,
  link,
}: SliderItemsProps) {
  const align = headline?.align ?? null;
  const alignClass =
    align === "center" ? "text-center" : align === "right" ? "text-right" : "";

  const slides = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const [containerRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      loop: true,
      renderMode: "precision",
      mode: "free-snap",
      slides: { perView: 3, spacing: 32 },
      drag: true,
      breakpoints: {
        "(max-width: 1024px)": { slides: { perView: 2, spacing: 24 } },
        "(max-width: 640px)": { slides: { perView: 1, spacing: 16 } },
      },
      created: (s) => {
        if (s.slides[0]) {
          const first = s.container.querySelector("[data-first-slide]");
          if (first) first.setAttribute("data-priority", "true");
        }
      },
    },
    [FadePlugin],
  );

  const prev = () => instanceRef.current?.prev();
  const next = () => instanceRef.current?.next();

  return (
    <section
      id={slugifyTitle(headline?.heading?.title)}
      className="max-w-7xl reveal-stagger mx-auto relative xl:py-24 md:py-18 py-14 flex flex-col xl:gap-18 md:gap-14 gap-10 w-full"
      aria-label={headline?.heading?.title || "Slider items"}
    >
      <div
        className={`flex md:flex-row md:flex-nowrap flex-wrap gap-8 justify-between ${alignClass}`}
      >
        <Heading
          level={headline?.heading?.level || "h3"}
          className="xl:text-7xl md:text-6xl text-5xl max-w-2xl uppercase font-noto reveal"
          title={headline?.heading?.title || ""}
        />
        <div className="max-w-lg tracking-tight reveal-item">
          {Array.isArray(headline?.description) ? (
            <BlocksRenderer content={headline?.description} />
          ) : null}
        </div>
      </div>

      <div className="relative">
        <div ref={containerRef} className="keen-slider">
          {slides.map((card, idx) => {
            const raw =
              card.image?.formats?.large?.url ||
              card.image?.formats?.medium?.url ||
              card.image?.formats?.small?.url ||
              card.image?.url ||
              "";
            const src = absoluteCmsUrl(raw);
            const alt =
              card.image?.alternativeText ||
              card.image?.caption ||
              card.image?.name ||
              card.title;
            return (
              <div key={card.id} className="keen-slider__slide">
                {card.url ? (
                  <Link
                    href={card.url}
                    className="flex flex-col gap-5 reveal-item"
                  >
                    <div className="relative w-full h-[535px]">
                      {src ? (
                        <BaseImage
                          src={src}
                          alt={alt}
                          fill
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          priority={idx === 0}
                          className="object-cover object-center"
                        />
                      ) : null}
                    </div>
                    {card.subtitle && (
                      <span className="text-xs uppercase font-medium">
                        {card.subtitle}
                      </span>
                    )}
                    <div className="flex flex-col gap-2.5">
                      <Heading
                        level="h4"
                        className="text-2xl font-noto uppercase"
                        title={card.title}
                      />
                      <div className="tracking-tight max-w-sm">
                        {Array.isArray(card.description) ? (
                          <BlocksRenderer content={card.description} />
                        ) : (
                          card.description
                        )}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex flex-col gap-5 reveal-item cursor-default">
                    <div className="relative w-full h-[535px]">
                      {src ? (
                        <BaseImage
                          src={src}
                          alt={alt}
                          fill
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          priority={idx === 0}
                          className="object-cover object-center"
                        />
                      ) : null}
                    </div>
                    {card.subtitle && (
                      <span className="text-xs uppercase font-medium">
                        {card.subtitle}
                      </span>
                    )}
                    <div className="flex flex-col gap-2.5">
                      <Heading
                        level="h4"
                        className="text-2xl font-noto uppercase"
                        title={card.title}
                      />
                      <div className="tracking-tight max-w-sm">
                        {Array.isArray(card.description) ? (
                          <BlocksRenderer content={card.description} />
                        ) : (
                          card.description
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-row justify-between items-center">
        {link?.url ? (
          <Link
            className="relative hover:bg-[#213932] flex justify-center min-w-28 z-10 py-3 px-4 flex items-center gap-2 text-sm uppercase bg-primary text-white w-fit transition rounded-4xl"
            href={link.url}
            target={link.target === "_blank" ? "_blank" : undefined}
            rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
            aria-label={link.title}
          >
            {link.title}
          </Link>
        ) : (
          <span />
        )}
        <div className="flex flex-row gap-4" aria-label="Slider controls">
          <button
            type="button"
            onClick={prev}
            className="w-12 h-12 cursor-pointer hover:bg-primary hover:text-white border border-grey-200 flex justify-center items-center"
            aria-label="Previous slide"
          >
            <Icon size={20} name="arrow-left" />
          </button>
          <button
            type="button"
            onClick={next}
            className="w-12 h-12 cursor-pointer hover:bg-primary hover:text-white border border-grey-200 flex justify-center items-center"
            aria-label="Next slide"
          >
            <Icon size={20} name="arrow-right" />
          </button>
        </div>
      </div>
    </section>
  );
}
